import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import { isSandboxUser, isSandboxAllowedPath } from './lib/sandbox-access';
import SandboxRestricted from './pages/SandboxRestricted';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DispatchBuilder from './pages/DispatchBuilder';
import Dispatches from './pages/Dispatches';
import DispatchView from './pages/DispatchView';
import Customers from './pages/Customers';
import CoBranding from './pages/CoBranding';
import Products from './pages/Products';
import Batches from './pages/Batches';
import Transformations from './pages/Transformations';
import WeeklyPlanner from './pages/WeeklyPlanner';
import SalesOrderEntry from './pages/SalesOrderEntry';
import PalletLabelDesigner from './pages/PalletLabelDesigner';
import Reports from './pages/Reports';
import UserManagement from './pages/UserManagement';
import Settings from './pages/Settings';
import BackupSettings from './pages/BackupSettings';
import Profile from './pages/Profile';
import DeletedItems from './pages/DeletedItems';
import QCManagement from './pages/QCManagement';
import DataManagement from './pages/DataManagement';
import CustomerProductCodes from './pages/CustomerProductCodes';
import CustomerProductFormulaPage from './pages/CustomerProductFormulaPage';
import CustomerCodeTraceReport from './pages/CustomerCodeTraceReport';
import ComplianceChecklist from './pages/ComplianceChecklist';
import TemplateDesigner from './pages/TemplateDesigner';
import DeploymentDashboard from './pages/DeploymentDashboard';
import DatabaseHost from './pages/DatabaseHost';
import ApiSettings from './pages/data-management/ApiSettings';
import SalesOrderPurge from './pages/data-management/SalesOrderPurge';
import QuickOrder from './pages/QuickOrder';
import AccountSettings from './pages/AccountSettings';
import ComplianceCenter from './pages/ComplianceCenter';
import SecurityAuditDashboard from './pages/SecurityAuditDashboard';
import BrochureSales from './pages/BrochureSales';
import BrochureSalesV4 from './pages/BrochureSalesV4';
import BrochureSalesV5 from './pages/BrochureSalesV5';
import BrochureBuyerPack from './pages/BrochureBuyerPack';
import BrochureBusinessPlan from './pages/BrochureBusinessPlan';
import BrochureBusinessPlanBH from './pages/BrochureBusinessPlanBH';
import BrochureBusinessPlanAD from './pages/BrochureBusinessPlanAD';
import BrochureConceptDeckAD from './pages/BrochureConceptDeckAD';
import MarketingPlaybook from './pages/MarketingPlaybook';
import SalesPrimer from './pages/SalesPrimer';
import BrandAssets from './pages/BrandAssets';
import LogoRender from './pages/LogoRender';
import FoundingCustomerPDF from './pages/FoundingCustomerPDF';
import StrategyReport from './pages/StrategyReport';
import CashelBlueCaseStudyPDF from './pages/CashelBlueCaseStudyPDF';
import SiteHome from './pages/site/SiteHome';
import SiteAbout from './pages/site/SiteAbout';
import SiteFeatures from './pages/site/SiteFeatures';
import SiteHowItWorks from './pages/site/SiteHowItWorks';
import SitePricing from './pages/site/SitePricing';
import FoundersClubPage from './pages/site/FoundersClubPage';
import PressPage from './pages/site/PressPage';
import BrochureInvestor from './pages/BrochureInvestor';
import SiteContact from './pages/site/SiteContact';
import SitePrivacy from './pages/site/SitePrivacy';
import SiteLegal from './pages/site/SiteLegal';
import SiteSecurity from './pages/site/SiteSecurity';
import Permissions from './pages/legal/Permissions';
import SubProcessors from './pages/legal/SubProcessors';
import NotFound from './pages/site/NotFound';
import LoginManage from './pages/admin/LoginManage';
import AdminConsole from './pages/admin/AdminConsole';
import AnalyticsInjector from './components/AnalyticsInjector';
import SeoLandingPage from './pages/site/SeoLandingPage';
import GuidesIndex from './pages/site/GuidesIndex';
import GuideArticle from './pages/site/GuideArticle';
import CashelBlueStoryPage from './pages/site/CashelBlueStoryPage';
import IpLogs from './pages/IpLogs';
import ScrollToTop from './components/ScrollToTop';
import VisitTracker from './components/VisitTracker';
import LangBootstrap from './components/LangBootstrap';
// Phase 1, Inwards / Production / Traceability
import Suppliers from './pages/inwards/Suppliers';
import SupplierProductCodes from './pages/inwards/SupplierProductCodes';
import InwardDeliveries from './pages/inwards/InwardDeliveries';
import MilkIntake from './pages/inwards/MilkIntake';
import Silos from './pages/inwards/Silos';
import MakeDays from './pages/production/MakeDays';
import MakeDayEdit from './pages/production/MakeDayEdit';
import BackwardTrace from './pages/traceability/BackwardTrace';
import ForwardTrace from './pages/traceability/ForwardTrace';
import MockRecallReport from './pages/reports/MockRecallReport';
import TraceEventsList from './pages/traceability/TraceEventsList';
import TraceEventDetail from './pages/traceability/TraceEventDetail';
import { MakeDaySummaryReport, MilkIntakeUsageReport, SupplierLotUsageReport, BackwardTraceReport, ForwardTraceReport } from './pages/reports/PhaseReports';
import { Loader2 } from 'lucide-react';

function ProtectedRoute({ children, adminOnly = false, allowDpo = false }) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // ── Public Sandbox hard route guard ──
  // Sandbox users see all menu items, but non-allowed routes render the
  // SandboxRestricted placeholder *instead of* the real page. The protected
  // component never mounts → no DOM tree to remove via DevTools.
  if (isSandboxUser(user) && !isSandboxAllowedPath(location.pathname)) {
    return <Layout><SandboxRestricted /></Layout>;
  }

  if (adminOnly) {
    const allowed = ['super_admin', 'admin'];
    if (allowDpo) allowed.push('dpo');
    if (!allowed.includes(user?.role)) {
      return <Navigate to="/app" replace />;
    }
  }

  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  // Note: we do NOT gate the whole <Routes> on auth `loading` here.
  // That used to show a spinner on the homepage and every public marketing
  // page while the initial /api/auth/me request was in flight, which is
  // exactly what crawlers (Google URL Inspection, social card previews)
  // were capturing instead of the real page. ProtectedRoute below still
  // shows the spinner for authenticated routes while auth resolves.
  return (
    <Routes>
      <Route 
        path="/login" 
        element={<Login />}
      />
      <Route path="/reset-password" element={<Login />} />

      {/* Public marketing brochure & internal documents */}
      <Route path="/brochure" element={<Navigate to="/brochure/sales" replace />} />
      <Route path="/brochure/executive" element={<Navigate to="/brochure/sales" replace />} />
      <Route path="/brochure/sales" element={<BrochureSales />} />
      <Route path="/brochure/sales/v4" element={<BrochureSalesV4 />} />
      <Route path="/brochure/sales/v5" element={<BrochureSalesV5 />} />
      <Route path="/brochure/bplan" element={<BrochureBusinessPlan />} />
      <Route path="/brochure/bplan/bh" element={<BrochureBusinessPlanBH />} />
      <Route path="/brochure/bplan/ad" element={<BrochureBusinessPlanAD />} />
      <Route path="/brochure/concept-deck/ad" element={<BrochureConceptDeckAD />} />
      <Route path="/brochure/buyer-pack" element={<BrochureBuyerPack />} />
      <Route path="/marketing-playbook" element={<MarketingPlaybook />} />
      <Route path="/brochure/sales-primer" element={<SalesPrimer />} />
      <Route path="/brand" element={<BrandAssets />} />
      <Route path="/logo-render" element={<LogoRender />} />
      <Route path="/founding-customer" element={<FoundingCustomerPDF />} />
      <Route path="/founders-club" element={<FoundersClubPage />} />
      <Route path="/launch-partner" element={<FoundersClubPage />} />
      <Route path="/press" element={<PressPage />} />
      <Route path="/brochure/investor" element={<BrochureInvestor />} />
      <Route path="/strategy" element={<StrategyReport />} />
      <Route path="/case-study/cashel-blue" element={<CashelBlueCaseStudyPDF />} />

      {/* Public marketing site, / is the public landing, /site/* are the deeper pages */}
      <Route path="/" element={<SiteHome />} />
      <Route path="/site" element={<Navigate to="/" replace />} />
      <Route path="/site/features" element={<SiteFeatures />} />
      <Route path="/site/how-it-works" element={<SiteHowItWorks />} />
      <Route path="/site/about" element={<SiteAbout />} />
      <Route path="/site/pricing" element={<SitePricing />} />
      <Route path="/site/contact" element={<SiteContact />} />
      <Route path="/site/privacy" element={<SitePrivacy />} />
      <Route path="/site/legal" element={<SiteLegal />} />
      <Route path="/site/security" element={<SiteSecurity />} />
      <Route path="/legal/permissions" element={<Permissions />} />
      <Route path="/legal/sub-processors" element={<SubProcessors />} />
      <Route path="/loginmanage" element={<LoginManage />} />
      <Route path="/admin" element={<AdminConsole />} />

      {/* SEO landing pages, config-driven, all routes share <SeoLandingPage /> */}
      <Route path="/food-traceability-software-ireland" element={<SeoLandingPage />} />
      <Route path="/excel-vs-keystone" element={<SeoLandingPage />} />
      <Route path="/keystone-vs-competitors" element={<SeoLandingPage />} />
      <Route path="/batch-traceability-software" element={<SeoLandingPage />} />
      <Route path="/dairy-cheese-production-software" element={<Navigate to="/cheese-production-software" replace />} />
      <Route path="/coa-generator-food-manufacturing" element={<SeoLandingPage />} />
      <Route path="/mock-recall-software-food-producers" element={<SeoLandingPage />} />
      <Route path="/brcgs-traceability-software" element={<SeoLandingPage />} />
      <Route path="/food-dispatch-note-pallet-label-software" element={<SeoLandingPage />} />
      <Route path="/accountsiq-keystone" element={<SeoLandingPage />} />
      <Route path="/sage-keystone" element={<SeoLandingPage />} />
      <Route path="/dairy-traceability-software-ireland" element={<SeoLandingPage />} />
      <Route path="/cheese-production-software" element={<SeoLandingPage />} />
      <Route path="/haccp-traceability-software" element={<SeoLandingPage />} />
      <Route path="/food-production-planning-software-ireland" element={<SeoLandingPage />} />
      <Route path="/vs-softtrace" element={<SeoLandingPage />} />
      <Route path="/vs-csb" element={<SeoLandingPage />} />
      <Route path="/vs-fooddocs" element={<SeoLandingPage />} />
      <Route path="/vs-diomac" element={<SeoLandingPage />} />
      <Route path="/vs-sage-x3" element={<SeoLandingPage />} />
      <Route path="/vs-aptean-food-beverage" element={<SeoLandingPage />} />
      <Route path="/vs-mrpeasy" element={<SeoLandingPage />} />
      <Route path="/mrpeasy-vs-keystone" element={<SeoLandingPage />} />
      <Route path="/vs-wherefour" element={<SeoLandingPage />} />
      <Route path="/food-dispatch-note-software" element={<SeoLandingPage />} />
      <Route path="/pallet-label-software-food-manufacturing" element={<SeoLandingPage />} />
      <Route path="/batch-tracking-software-cheese-producers" element={<Navigate to="/cheese-production-software" replace />} />
      <Route path="/farmhouse-cheese-production-software" element={<Navigate to="/cheese-production-software" replace />} />
      <Route path="/artisan-dairy-production-software" element={<Navigate to="/dairy-traceability-software-ireland" replace />} />
      <Route path="/retailer-dispatch-software-food-producers" element={<SeoLandingPage />} />
      <Route path="/customer-sku-mapping-food-manufacturing" element={<SeoLandingPage />} />
      <Route path="/founder-led-implementation" element={<SeoLandingPage />} />
      <Route path="/irish-food-producer-audit-readiness-checklist" element={<SeoLandingPage />} />
      <Route path="/bakery-traceability-software" element={<SeoLandingPage />} />
      <Route path="/meat-processing-traceability-software" element={<SeoLandingPage />} />
      <Route path="/seafood-traceability-software" element={<SeoLandingPage />} />
      <Route path="/prepared-food-manufacturing-software" element={<SeoLandingPage />} />
      <Route path="/food-wholesale-traceability-software" element={<SeoLandingPage />} />
      <Route path="/food-traceability-software-uk" element={<SeoLandingPage />} />
      <Route path="/salsa-certification-software" element={<SeoLandingPage />} />
      <Route path="/food-recall-management-software" element={<SeoLandingPage />} />
      <Route path="/allergen-management-food-manufacturing" element={<SeoLandingPage />} />
      <Route path="/gs1-128-sscc-label-software" element={<SeoLandingPage />} />
      <Route path="/food-erp-small-business" element={<SeoLandingPage />} />
      <Route path="/goods-in-software-food" element={<SeoLandingPage />} />
      <Route path="/softtrace-alternative" element={<SeoLandingPage />} />
      <Route path="/mrpeasy-alternative" element={<SeoLandingPage />} />
      <Route path="/wherefour-alternative" element={<SeoLandingPage />} />

      {/* Guides hub, long-form SEO articles, config-driven from guidesConfig.js */}
      <Route path="/guides" element={<GuidesIndex />} />
      <Route path="/guides/:slug" element={<GuideArticle />} />

      {/* Localized marketing content (LLM-translated, cached, /hu /pl /de) */}
      {['hu', 'pl', 'de'].map((l) => (
        <React.Fragment key={l}>
          {/* Core marketing pages, localized URL variants for SEO */}
          <Route path={`/${l}`} element={<SiteHome />} />
          <Route path={`/${l}/site/features`} element={<SiteFeatures />} />
          <Route path={`/${l}/site/how-it-works`} element={<SiteHowItWorks />} />
          <Route path={`/${l}/site/about`} element={<SiteAbout />} />
          <Route path={`/${l}/site/pricing`} element={<SitePricing />} />
          <Route path={`/${l}/site/contact`} element={<SiteContact />} />
          <Route path={`/${l}/site/security`} element={<SiteSecurity />} />
          {/* Guides + config-driven SEO landing pages */}
          <Route path={`/${l}/guides`} element={<GuidesIndex />} />
          <Route path={`/${l}/guides/:slug`} element={<GuideArticle />} />
          <Route path={`/${l}/:slug`} element={<SeoLandingPage />} />
        </React.Fragment>
      ))}

      {/* HTML case study (PDF route stays at /case-study/cashel-blue) */}
      <Route path="/case-study/cashel-blue-story" element={<CashelBlueStoryPage />} />

      {/* Private, IP / visitor logs (password-gated, noindex) */}
      <Route path="/ip-logs" element={<IpLogs />} />

      {/* Authenticated app, moved to /app to keep the public preview clean */}
      <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/quick-order" element={<ProtectedRoute><QuickOrder /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
      <Route path="/sales-order/new" element={<ProtectedRoute><SalesOrderEntry /></ProtectedRoute>} />
      <Route path="/sales-order/:id" element={<ProtectedRoute><SalesOrderEntry /></ProtectedRoute>} />
      <Route path="/dispatches" element={<ProtectedRoute><Dispatches /></ProtectedRoute>} />
      <Route path="/dispatch/new" element={<ProtectedRoute><DispatchBuilder /></ProtectedRoute>} />
      <Route path="/dispatch/:id" element={<ProtectedRoute><DispatchBuilder /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/cobranding" element={<ProtectedRoute><CoBranding /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
      <Route path="/batches" element={<ProtectedRoute><Batches /></ProtectedRoute>} />
      <Route path="/qc" element={<ProtectedRoute><QCManagement /></ProtectedRoute>} />
      <Route path="/transformations" element={<ProtectedRoute><Transformations /></ProtectedRoute>} />
      <Route path="/planner" element={<ProtectedRoute><WeeklyPlanner /></ProtectedRoute>} />
      <Route path="/dispatch-view" element={<ProtectedRoute><DispatchView /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplateDesigner /></ProtectedRoute>} />
      <Route path="/pallet-labels/:dispatchId" element={<ProtectedRoute><PalletLabelDesigner /></ProtectedRoute>} />
      <Route path="/pallet-labels" element={<ProtectedRoute><PalletLabelDesigner /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/reports/compliance" element={<ProtectedRoute><ComplianceChecklist /></ProtectedRoute>} />
      <Route path="/supplier-codes" element={<ProtectedRoute><CustomerProductCodes /></ProtectedRoute>} />
      <Route path="/supplier-codes/:mappingId/formula" element={<ProtectedRoute><CustomerProductFormulaPage /></ProtectedRoute>} />
      <Route path="/supplier-trace" element={<ProtectedRoute><CustomerCodeTraceReport /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

      {/* Inwards (Phase 1) */}
      <Route path="/inwards/suppliers" element={<ProtectedRoute><Suppliers /></ProtectedRoute>} />
      <Route path="/inwards/supplier-codes" element={<ProtectedRoute><SupplierProductCodes /></ProtectedRoute>} />
      <Route path="/inwards/deliveries" element={<ProtectedRoute><InwardDeliveries /></ProtectedRoute>} />
      <Route path="/inwards/milk-intake" element={<ProtectedRoute><MilkIntake /></ProtectedRoute>} />
      <Route path="/inwards/silos" element={<ProtectedRoute><Silos /></ProtectedRoute>} />
      {/* Production */}
      <Route path="/production/make-days" element={<ProtectedRoute><MakeDays /></ProtectedRoute>} />
      <Route path="/production/make-days/new" element={<ProtectedRoute><MakeDayEdit /></ProtectedRoute>} />
      <Route path="/production/make-days/:id/edit" element={<ProtectedRoute><MakeDayEdit /></ProtectedRoute>} />
      {/* Traceability */}
      <Route path="/traceability/backward" element={<ProtectedRoute><BackwardTrace /></ProtectedRoute>} />
      <Route path="/traceability/forward" element={<ProtectedRoute><ForwardTrace /></ProtectedRoute>} />
      <Route path="/traceability/recall" element={<ProtectedRoute><MockRecallReport /></ProtectedRoute>} />
      <Route path="/traceability/events" element={<ProtectedRoute><TraceEventsList /></ProtectedRoute>} />
      <Route path="/traceability/events/:id" element={<ProtectedRoute><TraceEventDetail /></ProtectedRoute>} />
      {/* Reports (Phase 2) */}
      <Route path="/reports/make-day-summary" element={<ProtectedRoute><MakeDaySummaryReport /></ProtectedRoute>} />
      <Route path="/reports/milk-intake-usage" element={<ProtectedRoute><MilkIntakeUsageReport /></ProtectedRoute>} />
      <Route path="/reports/supplier-lot-usage" element={<ProtectedRoute><SupplierLotUsageReport /></ProtectedRoute>} />
      <Route path="/reports/backward-trace" element={<ProtectedRoute><BackwardTraceReport /></ProtectedRoute>} />
      <Route path="/reports/forward-trace" element={<ProtectedRoute><ForwardTraceReport /></ProtectedRoute>} />

      {/* Admin routes */}
      <Route path="/users" element={<ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
      <Route path="/backup" element={<ProtectedRoute adminOnly><BackupSettings /></ProtectedRoute>} />
      <Route path="/deleted-items" element={<ProtectedRoute adminOnly><DeletedItems /></ProtectedRoute>} />
      <Route path="/admin/data-management" element={<ProtectedRoute adminOnly><DataManagement /></ProtectedRoute>} />
      <Route path="/admin/data-management/database-host" element={<ProtectedRoute adminOnly><DatabaseHost /></ProtectedRoute>} />
      <Route path="/admin/data-management/api-settings" element={<ProtectedRoute adminOnly><ApiSettings /></ProtectedRoute>} />
      <Route path="/admin/data-management/sales-order-purge" element={<ProtectedRoute adminOnly><SalesOrderPurge /></ProtectedRoute>} />
      <Route path="/admin/deployment" element={<ProtectedRoute adminOnly><DeploymentDashboard /></ProtectedRoute>} />
      <Route path="/admin/compliance" element={<ProtectedRoute adminOnly><ComplianceCenter /></ProtectedRoute>} />
      <Route path="/admin/security-audit" element={<ProtectedRoute adminOnly allowDpo><SecurityAuditDashboard /></ProtectedRoute>} />
      
      {/* Fallback, authenticated users go to the app; anonymous visitors see a real 404 with noindex so search engines don't treat unknown URLs as duplicate homepage content */}
      <Route path="*" element={isAuthenticated ? <Navigate to="/app" replace /> : <NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <VisitTracker />
        <AnalyticsInjector />
        <LangBootstrap />
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
